import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  AdminQueryKeys,
  getAiUsageMetrics,
  refreshModelPrices,
  type AiUsageMetricsFilter,
  type AiUsageMetricsResponse,
  type ModelPricesRefreshResponse,
} from '../api';

/**
 * React-Query wrapper around <c>GET /api/v1/admin/metrics/ai-usage</c>. The query key includes
 * the full filter so swapping a single parameter triggers a fresh fetch instead of returning a
 * stale aggregation.
 */
export function useAiUsageMetrics(filter: AiUsageMetricsFilter): UseQueryResult<AiUsageMetricsResponse> {
  return useQuery({
    queryKey: AdminQueryKeys.aiUsage(filter),
    queryFn: ({ signal }) => getAiUsageMetrics(filter, signal),
    staleTime: 60_000,
  });
}

/** Mutation that triggers a manual refresh of the upstream price catalogs. */
export function useRefreshModelPrices(): ReturnType<typeof useMutation<ModelPricesRefreshResponse, Error, void>> {
  const queryClient = useQueryClient();
  return useMutation<ModelPricesRefreshResponse, Error, void>({
    mutationFn: () => refreshModelPrices(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AdminQueryKeys.modelPrices() });
      // Aggregations would change once new prices apply to subsequent calls; invalidate too.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'metrics', 'ai-usage'] });
    },
  });
}
